import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from '../components/AuthLayout';
import PasswordInput from '../components/PasswordInput';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';

function isInvitePreview(value: unknown): value is { email: string } {
  return !!value && typeof value === 'object' && typeof (value as { email?: unknown }).email === 'string';
}

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const userId = params.get('uid') || '';
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!token || !userId) {
        setError('This invite link is invalid or expired.');
        setLoading(false);
        return;
      }
      const res = await fetch(`/api/auth/invite?${new URLSearchParams({ token, uid: userId }).toString()}`);
      const data: unknown = await res.json().catch(() => null);
      if (!res.ok || !isInvitePreview(data)) {
        setError('This invite link is invalid or expired.');
        setLoading(false);
        return;
      }
      setEmail(data.email);
      setLoading(false);
    })();
  }, [token, userId]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    const res = await fetch('/api/auth/invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, token, password }),
    });
    if (!res.ok) {
      setError('This invite link is invalid or expired.');
      return;
    }
    navigate('/', { replace: true });
  }

  return (
    <AuthLayout title="Create your account">
      {loading && <p className="text-sm text-muted-foreground">Checking invite…</p>}
      {!loading && error && !email && <p className="text-sm text-destructive">{error}</p>}
      {!loading && email && (
        <form className="space-y-4" onSubmit={submit}>
          <p className="text-sm text-muted-foreground">Set a password for <span className="text-foreground">{email}</span>.</p>
          <div className="grid gap-2">
            <Label htmlFor="password">Password</Label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={8}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="confirm">Confirm password</Label>
            <PasswordInput
              id="confirm"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              required
              minLength={8}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full">Create account</Button>
        </form>
      )}
    </AuthLayout>
  );
}

